import {makeAutoObservable, observable} from 'mobx'
import {Lang} from 'yougile-lang'
import {Checklist, ChecklistItem, Hub} from 'yougile-model'
import {PermissionsChecker} from '@client/permissions-checker'
import {Tracking} from '@common/tracking'
import {TrackingEvent} from '@common/tracking-events'
import {TextAreaApi} from '@components/common/base/textarea/types'
import {MessageBox} from '@components/message-box'
import {createUseStore} from '@containers/hooks'
import {Task} from '@extensions/task'
import {ChecklistsStore} from '../../../checklists-store'

interface State {
  editing: boolean
  unsavedTitle?: string
}

export class ChecklistItemStore {
  state: State = {
    editing: false
  }

  private task: Hub
  private list: Checklist
  private item: ChecklistItem

  constructor(private checklistsStore: ChecklistsStore) {
    makeAutoObservable(
      this,
      {
        state: observable.shallow
      },
      {autoBind: true}
    )
  }

  syncOnNew(task: Hub, list: Checklist, item: ChecklistItem): void {
    this.task = task
    this.list = list
    this.item = item
  }

  startEditing(): void {
    const isTaskTemplate = Task.isTaskTemplate(this.task)
    if (isTaskTemplate || PermissionsChecker.checkEditSubtasks(this.task)) {
      this.state.editing = true
      this.checklistsStore.updateEditInProgress(true)
    }
  }
  onChangeTitle(api: TextAreaApi): void {
    this.state.unsavedTitle = api.text
  }
  submitEditing(): void {
    if (!this.state.editing) {
      return
    }
    void Tracking.send(TrackingEvent.taskEditSubtasks)
    const text = this.state.unsavedTitle
    if (text) {
      Task.renameSubtask(this.task, this.list.id, this.item.id, text)
    } else if (text === '') {
      Task.deleteSubtask(this.task, this.list.id, this.item.id)
    }

    this.state.unsavedTitle = undefined
    this.state.editing = false
    this.checklistsStore.updateEditInProgress(false)
  }

  cancelEditing(): void {
    this.state.unsavedTitle = undefined
    this.state.editing = false
    this.checklistsStore.updateEditInProgress(false)
  }

  blurEditing(api: TextAreaApi): void {
    if (!this.state.editing) {
      return
    }
    const text = api.text.trim()
    if (text) {
      if (this.item.title !== text) {
        this.state.unsavedTitle = text
      } else {
        this.state.unsavedTitle = undefined
      }
    }
    this.state.editing = false
    this.checklistsStore.updateEditInProgress(false)
  }

  toggleItemCompleted(): void {
    const isTaskTemplate = Task.isTaskTemplate(this.task)
    if (
      !isTaskTemplate &&
      !PermissionsChecker.checkCompleteSubtasks(this.task)
    ) {
      MessageBox.warn(Lang.msg.permissionsForbiddenEditSubtasks)
      return
    }
    Task.toggleSubtaskCompleted(this.task, this.list.id, this.item.id)
  }

  delete(): void {
    if (!this.state.editing) {
      return
    }
    this.state.editing = false
    this.checklistsStore.updateEditInProgress(false)
    Task.deleteSubtask(this.task, this.list.id, this.item.id)
  }
}

export const [ChecklistItemContext, useChecklistItemStore] =
  createUseStore(ChecklistItemStore)
